export type MessagePrimitive = string | number | boolean | null | undefined;
export type MessageValues = Readonly<Record<string, MessagePrimitive>>;
export type MessageCatalog = Readonly<Record<string, string>>;
export type PartialMessageCatalog = Readonly<Record<string, string | undefined>>;

export interface MessageCatalogInspection {
  missingKeys: readonly string[];
  extraKeys: readonly string[];
  emptyKeys: readonly string[];
  valid: boolean;
}

/** Inspect a localized catalog against the canonical base catalog. */
export function inspectMessageCatalog(
  baseCatalog: MessageCatalog,
  candidateCatalog: PartialMessageCatalog,
): MessageCatalogInspection {
  const baseKeys = Object.keys(baseCatalog).sort();
  const candidateKeys = Object.keys(candidateCatalog).sort();
  const baseSet = new Set(baseKeys);
  const candidateSet = new Set(candidateKeys);

  const missingKeys = baseKeys.filter((key) => !candidateSet.has(key));
  const extraKeys = candidateKeys.filter((key) => !baseSet.has(key));
  const emptyKeys = candidateKeys.filter((key) => {
    const value = candidateCatalog[key];
    return typeof value !== 'string' || value.trim().length === 0;
  });

  return {
    missingKeys,
    extraKeys,
    emptyKeys,
    valid: missingKeys.length === 0 && extraKeys.length === 0 && emptyKeys.length === 0,
  };
}

/** Replace `{name}` placeholders without evaluating HTML or code. */
export function interpolateMessage(template: string, values?: MessageValues): string {
  if (!values) return template;

  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (placeholder, key: string) => {
    if (!Object.prototype.hasOwnProperty.call(values, key)) return placeholder;
    const value = values[key];
    return value === null || value === undefined ? '' : String(value);
  });
}
